// frontend/src/components/MlExperiment/MlExperimentDetails.tsx

import React from 'react';

interface Props {
  analysis: any;
}

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Dicionário simples para traduzir termos técnicos
const TRADUCOES: Record<string, string> = {
  'NegBinomialTrainer': 'Treinador de Regressão Binomial Negativa',
  'AdvancedSklearnTrainer': 'Treinador Avançado (Ensemble)',
  'count_model': 'Contagem (ex: número de sinistros)',
  'classification': 'Classificação (ex: sim ou não)',
  'regression': 'Regressão (ex: preço ou valor)',
  'time_series': 'Série Temporal (ex: previsão ao longo do tempo)',
};

export const MlExperimentDetails: React.FC<Props> = ({ analysis }) => {
  const report = analysis.training_report;
  const metadata = report?.metadata;
  const hasMetadata = metadata && typeof metadata === 'object';

  const trainerName = metadata?.trainer || 'Desconhecido';
  const trainerTraduzido = TRADUCOES[trainerName] || trainerName;
  const problemType = metadata?.problem_type || analysis.problem_type || 'Desconhecido';
  const problemTraduzido = TRADUCOES[problemType] || problemType;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #1a4a7a',
      marginBottom: '24px',
    }}>
      <h3 style={{ color: '#4fc3f7', margin: '0 0 16px 0' }}>
        📋 Detalhes do Experimento
      </h3>

      {/* ============================================================ */}
      {/* CAMADA 1: RESUMO EM LINGUAGEM DE NEGÓCIO (NÃO TÉCNICA)       */}
      {/* ============================================================ */}
      <div style={{
        background: 'rgba(79, 195, 247, 0.08)',
        borderLeft: '3px solid #4fc3f7',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        color: '#e0e0e0',
        fontSize: '14px',
        lineHeight: 1.6,
      }}>
        <p style={{ margin: '0 0 8px 0' }}>
          💡 <strong style={{ color: '#4fc3f7' }}>O que isso significa:</strong>
        </p>
        <p style={{ margin: 0 }}>
          Este experimento usou o modelo <strong>{renderValue(report?.model_name)}</strong> para resolver um problema de
          <strong> {problemTraduzido}</strong>. O modelo foi treinado com <strong>{renderValue(metadata?.n_samples)} exemplos</strong> e
          analisou <strong>{renderValue(metadata?.n_features)} características</strong>.
          A métrica de avaliação escolhida foi <strong>{renderValue(report?.metric_name)}</strong>, que resultou no valor
          <strong> {renderValue(report?.metric_value)}</strong>.
        </p>
      </div>

      {/* ============================================================ */}
      {/* CAMADA 2: DADOS TÉCNICOS (FORMATADOS)                        */}
      {/* ============================================================ */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '12px', color: '#8899aa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
          📊 Relatório de Treinamento (Técnico)
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#8899aa' }}>Modelo</div>
            <div style={{ fontSize: '14px', color: '#ffffff' }}>{renderValue(report?.model_name)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#8899aa' }}>Treinador (Trainer)</div>
            <div style={{ fontSize: '14px', color: '#ffffff' }}>{renderValue(metadata?.trainer)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#8899aa' }}>Métrica</div>
            <div style={{ fontSize: '14px', color: '#ffffff' }}>{renderValue(report?.metric_name)} = {renderValue(report?.metric_value)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#8899aa' }}>Tipo de Problema</div>
            <div style={{ fontSize: '14px', color: '#ffffff' }}>{renderValue(metadata?.problem_type)}</div>
          </div>
        </div>

        {hasMetadata && (
          <div style={{ overflowX: 'auto', background: 'rgba(10, 10, 26, 0.4)', borderRadius: '8px', padding: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#ffffff' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1a4a7a' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#8899aa', fontWeight: 500 }}>Campo (Metadata)</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#8899aa', fontWeight: 500 }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(metadata).map(([key, value]) => (
                  <tr key={key} style={{ borderBottom: '1px solid #16213e' }}>
                    <td style={{ padding: '8px', color: '#8899aa' }}>{key}</td>
                    <td style={{ padding: '8px' }}>{renderValue(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* CAMADA 3: JSON CRU (RECOLHÍVEL)                              */}
      {/* ============================================================ */}
      <details style={{ marginTop: '16px' }}>
        <summary style={{ cursor: 'pointer', color: '#4fc3f7', fontSize: '13px' }}>
          🔍 Ver JSON bruto do relatório (para cientistas de dados)
        </summary>
        <pre style={{
          background: 'rgba(10, 10, 26, 0.6)',
          borderRadius: '8px',
          padding: '12px',
          color: '#e0e0e0',
          fontSize: '12px',
          fontFamily: 'monospace',
          overflowX: 'auto',
          maxHeight: '300px',
          marginTop: '8px',
        }}>
          {JSON.stringify(report, null, 2)}
        </pre>
      </details>
    </div>
  );
};